import FeatureGates from '@atlaskit/feature-gate-js-client';
import { useEffect, useCallback, useRef } from 'react';
import { useTrigger, useFirePostOfficeTrigger } from '@atlassian/post-office-context';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { ManualMessageViewedEvent } from '@post-office/placement-contexts/message-viewed-component';
import { useFireEngagementPlatformAnalyticsEvents } from '@post-office/analytics';
import { useChoreographerContext } from '@post-office/placement-contexts/create-placement-component/providers/stable-choreographer';
import { createRender } from './types';

// Custom hook for managing EP External message lifecycle through Post Office triggers
const useEPMessageLifecycle = (epMessageId: string) => {
	const triggers = {
		start: `${epMessageId}-start`,
		started: `${epMessageId}-started`,
		dismiss: `${epMessageId}-dismiss`,
		isDismissed: `${epMessageId}-dismissed`,
		ctaClick: `${epMessageId}-cta-click`,
	};

	// === TRIGGER CREATION ===
	const { fireTrigger: triggerStartedMessage } = useFirePostOfficeTrigger({
		triggerId: triggers.started,
	});
	const { fireTrigger: triggerDismissedMessage } = useFirePostOfficeTrigger({
		triggerId: triggers.isDismissed,
	});

	// === TRIGGER LISTENING ===
	// all but isStarted are triggered by the product frontend
	const { isActive: shouldStart } = useTrigger(triggers.start);
	const { isActive: isStarted } = useTrigger(triggers.started);
	const { isActive: shouldDismiss } = useTrigger(triggers.dismiss);
	const { isActive: isClicked } = useTrigger(triggers.ctaClick);

	const startMessage = useCallback(
		() => triggerStartedMessage({ isActive: true }),
		[triggerStartedMessage],
	);

	const dismissMessage = useCallback(
		() => triggerDismissedMessage({ isActive: true }),
		[triggerDismissedMessage],
	);

	return {
		// Actions
		startMessage,
		dismissMessage,
		// States
		shouldStart,
		isStarted,
		shouldDismiss,
		isClicked,
	};
};

export const render = createRender(
	({ epMessageId, onDismissed, messageInstanceId, userId, orgId, workspaceId, tenantId }) => {
		const { messageInstanceViewed, messageInstanceClicked } = useMessageInstanceEvent();
		const { startMessage, dismissMessage, shouldStart, isStarted, shouldDismiss, isClicked } =
			useEPMessageLifecycle(epMessageId);
		const { fireAnalyticsEvent } = useFireEngagementPlatformAnalyticsEvents();
		const { choreographerEnabled } = useChoreographerContext();

		const enableEPExternalMessageOnDismissed = FeatureGates.checkGate(
			'post-office_ssf-enable-new-dismissal-logic',
		);

		// Track if handleDismiss has been called to ensure it's only called once
		const hasDismissedRef = useRef(false);

		const handleDismiss = useCallback(() => {
			// Only call dismiss logic if it hasn't been called already
			if (hasDismissedRef.current) {
				return;
			}
			hasDismissedRef.current = true;
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'dismiss',
					clickedElement: 'button',
					buttonType: 'secondary',
				},
				action: { actionType: 'dismiss' },
				additionalMetadataFromFrontend: {
					epMessageId,
				},
			});
			fireAnalyticsEvent({
				messageType: 'external',
				messageId: epMessageId,
				userId,
				orgId,
				workspaceId,
				tenantId,
			});
			dismissMessage();
			// The current logic for onDismissed in the SSF placement dismisses all the messages in the placement
			// This is not appropriate for the EP external messages.
			// We have use-cases where multiple messages that need to show in a session, (i.e. spotlight tours)
			// This feature gate is tied to the new dismissal logic for SSF
			if (enableEPExternalMessageOnDismissed && messageInstanceId) {
				onDismissed(messageInstanceId);
			}
		}, [
			messageInstanceClicked,
			fireAnalyticsEvent,
			epMessageId,
			userId,
			orgId,
			workspaceId,
			choreographerEnabled,
			tenantId,
			onDismissed,
			messageInstanceId,
			dismissMessage,
		]);

		const handleCtaClick = useCallback(() => {
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'cta',
					clickedElement: 'button',
					buttonType: 'primary',
				},
				action: { actionType: 'open' },
				additionalMetadataFromFrontend: {
					epMessageId,
				},
			});
			dismissMessage();
		}, [messageInstanceClicked, dismissMessage]);

		useEffect(() => {
			// should start is triggered by the product frontend
			if (shouldStart) {
				startMessage();
			}
		}, [shouldStart, startMessage]);

		useEffect(() => {
			if (isStarted && messageInstanceViewed) {
				void messageInstanceViewed({
					epMessageId,
				});
			}
		}, [isStarted, messageInstanceViewed, epMessageId]);

		useEffect(() => {
			if (shouldDismiss) {
				handleDismiss();
			}
		}, [shouldDismiss, handleDismiss]);

		useEffect(() => {
			if (isClicked) {
				handleCtaClick();
			}
		}, [isClicked, handleCtaClick]);

		useEffect(() => {
			return () => {
				handleDismiss();
			};
		}, [handleDismiss]);

		if (isStarted) {
			return <ManualMessageViewedEvent />;
		}
		return null;
	},
);

export default render;
